import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { inputCls } from "./constants";
import { MediaInput } from "./MediaInput";

const lbl = "text-[11px] font-mono uppercase tracking-[0.1em] text-[#475467]";
const card = "rounded-[14px] border border-[#E4E9F0] bg-white";
const cardShadow = { boxShadow: "0 3px 12px rgba(15,23,42,0.035)" };

export function MarketsTab({ markets, onChanged }) {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) { toast.error("Market name is required"); return; }
    await api.post("/admin/markets", { name });
    setName(""); toast.success("Market added"); onChanged();
  };
  const toggle = async (m) => { await api.patch(`/admin/markets/${m.id}`, { active: !m.active }); onChanged(); };
  const remove = async (m) => { await api.delete(`/admin/markets/${m.id}`); onChanged(); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className={`${card} p-6 space-y-4`} style={cardShadow}>
        <h3 className="font-serif-display text-2xl text-[#101828]">Add market</h3>
        <div><Label className={lbl}>City / Region</Label><Input data-testid="market-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Miami" className={inputCls} onKeyDown={(e) => e.key === "Enter" && add()} /></div>
        <Button data-testid="market-add" className="w-full rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white" onClick={add}>Add market</Button>
      </div>
      <div className={`lg:col-span-2 ${card} divide-y divide-[#F1F2F4] overflow-hidden`} style={cardShadow}>
        {markets.map((m) => (
          <div key={m.id} data-testid={`admin-market-${m.id}`} className="p-4 flex items-center justify-between gap-4">
            <span className={`font-serif-display text-2xl uppercase ${m.active ? "text-[#101828]" : "text-[#94A3B8]"}`}>{m.name}</span>
            <div className="flex items-center gap-3">
              <Switch data-testid={`market-toggle-${m.id}`} checked={m.active} onCheckedChange={() => toggle(m)} />
              <Button variant="ghost" size="sm" data-testid={`market-delete-${m.id}`} onClick={() => remove(m)}><Trash2 className="h-4 w-4 text-red-500" strokeWidth={1.5} /></Button>
            </div>
          </div>
        ))}
        {markets.length === 0 && <p className="p-6 text-[#94A3B8] text-sm">No markets yet.</p>}
      </div>
    </div>
  );
}

const FIELDS = [
  ["brand_logo_url", "White brand logo (transparent PNG)", "image/*"],
  ["hero_headline", "Hero headline", "text"],
  ["hero_subline", "Hero supporting line", "text"],
  ["hero_video_url", "Hero video (mp4)", "video/*"],
  ["hero_image_url", "Hero fallback image (only shown if no video)", "image/*"],
  ["featured_film_url", "Featured film (mp4)", "video/*"],
  ["featured_film_poster", "Featured film poster", "image/*"],
  ["portal_image_url", "Client portal preview image", "image/*"],
  ["about_image_url", "About page image", "image/*"],
  ["cta_image_url", "Final CTA background image", "image/*"],
  ["instagram_url", "Instagram URL", "text"],
  ["instagram_handle", "Instagram handle (shown on homepage)", "text"],
  ["contact_email", "Contact email", "text"],
  ["contact_phone", "Contact phone", "text"],
];

export function SiteTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/site-settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/site-settings", s); setS(r.data); toast.success("Site settings saved — refresh the public site to see changes"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  if (!s) return null;
  return (
    <div className="grid lg:grid-cols-5 gap-6 items-start">
      <div className={`lg:col-span-3 ${card} p-6 space-y-5`} style={cardShadow}>
        <h3 className="font-serif-display text-2xl text-[#101828]">Homepage media & copy</h3>
        <p className="text-sm text-[#475467]">Paste a URL or upload a file. Videos should be short, muted-friendly mp4 clips (ideally under 15 MB) for fast loading.</p>
        {FIELDS.map(([k, label, kind]) => kind === "text" ? (
          <div key={k}><Label className={lbl}>{label}</Label><Input data-testid={`site-${k}`} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={inputCls} /></div>
        ) : (
          <MediaInput key={k} label={label} testid={`site-${k}`} value={s[k]} onChange={(v) => setS({ ...s, [k]: v })} accept={kind} />
        ))}
        <Button data-testid="site-save" disabled={saving} className="rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white px-8" onClick={save}>{saving ? "Saving…" : "Save settings"}</Button>
      </div>
      <InstagramManager />
    </div>
  );
}

function InstagramManager() {
  const [posts, setPosts] = useState([]);
  const [image, setImage] = useState("");
  const [link, setLink] = useState("");
  const load = () => api.get("/admin/instagram").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!image) { toast.error("Add an image first"); return; }
    await api.post("/admin/instagram", { image, link });
    setImage(""); setLink(""); toast.success("Post added to strip"); load();
  };
  const remove = async (p) => { await api.delete(`/admin/instagram/${p.id}`); load(); };
  const move = async (p, direction) => { const r = await api.patch(`/admin/instagram/${p.id}/move`, { direction }); setPosts(r.data); };

  return (
    <div className={`lg:col-span-2 ${card} p-6 space-y-5`} style={cardShadow}>
      <div>
        <h3 className="font-serif-display text-2xl text-[#101828]">Instagram strip</h3>
        <p className="text-sm text-[#475467] mt-1">Curate the 6 posts shown on the homepage (first 6 in order). Each tile links to the post, or your profile if blank.</p>
      </div>
      <MediaInput label="Post image" testid="ig-image" value={image} onChange={setImage} accept="image/*" />
      <div><Label className={lbl}>Post link (optional)</Label><Input data-testid="ig-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://www.instagram.com/p/…" className={inputCls} /></div>
      <Button data-testid="ig-add" className="w-full rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white" onClick={add}>Add post</Button>
      <div className="grid grid-cols-3 gap-2">
        {posts.map((p, i) => (
          <div key={p.id} data-testid={`ig-post-${p.id}`} className="group relative aspect-square bg-[#F7F9FC] overflow-hidden rounded-xl">
            <img src={p.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
              <button data-testid={`ig-up-${p.id}`} disabled={i === 0} onClick={() => move(p, "up")} className="h-7 w-7 rounded-lg bg-white text-[#101828] disabled:opacity-30"><ChevronLeft className="h-4 w-4 mx-auto" /></button>
              <button data-testid={`ig-delete-${p.id}`} onClick={() => remove(p)} className="h-7 w-7 rounded-lg bg-white text-red-600"><Trash2 className="h-4 w-4 mx-auto" strokeWidth={1.5} /></button>
              <button data-testid={`ig-down-${p.id}`} disabled={i === posts.length - 1} onClick={() => move(p, "down")} className="h-7 w-7 rounded-lg bg-white text-[#101828] disabled:opacity-30"><ChevronRight className="h-4 w-4 mx-auto" /></button>
            </div>
          </div>
        ))}
      </div>
      {posts.length === 0 && <p className="text-sm text-[#94A3B8]">No posts yet — the strip is hidden on the homepage.</p>}
    </div>
  );
}
